import * as React from 'react';
import {
  IBac, IPlanningEntry, IBacActionCreatePayload, BacActionStatus, BacActionType,
} from '../../models';
import { useData } from '../../core/context/ProvidersContext';
import { formatMonthLabel } from '../shared/MonthUtils';
import styles from './BacActionModal.module.scss';

export interface IBacActionModalProps {
  bac: IBac;
  type: BacActionType;
  planningEntry?: IPlanningEntry;
  onSaved: () => void;
  onCancel: () => void;
}

const STATUS_OPTIONS: { value: BacActionStatus; label: string; hint: string }[] = [
  { value: 'confirme',    label: 'Confirmé',           hint: 'Le dépôt / client a confirmé. L\'action est close.' },
  { value: 'en_attente',  label: 'En attente retour',  hint: 'En attente d\'une réponse ou d\'un retour.' },
  { value: 'replanifie',  label: 'Replanifié',         hint: 'L\'intervention a été déplacée à un autre mois.' },
  { value: 'bloque',      label: 'Bloqué',             hint: 'Un point bloquant est à traiter avant de pouvoir clôturer.' },
];

const TYPE_LABELS: Record<BacActionType, { verb: string; title: string; icon: string }> = {
  relance_depot: { verb: 'Point avec le dépôt', title: 'Point dépôt', icon: '📞' },
  info_clients:  { verb: 'Communication client',  title: 'Info clients', icon: '✉️' },
};

function toInputDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export const BacActionModal: React.FC<IBacActionModalProps> = ({
  bac, type, planningEntry, onSaved, onCancel,
}) => {
  const data = useData();
  const meta = TYPE_LABELS[type];

  const [status, setStatus] = React.useState<BacActionStatus>('confirme');
  const [comment, setComment] = React.useState('');
  const [doneAt, setDoneAt] = React.useState<string>(toInputDate(new Date()));
  const [interventionMonth, setInterventionMonth] = React.useState<string>(
    planningEntry ? planningEntry.Mois.substring(0, 7) : ''
  );
  const [file, setFile] = React.useState<File | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const originalInterventionMonth = planningEntry
    ? planningEntry.Mois.substring(0, 7)
    : '';
  const hasInterventionShift = planningEntry && interventionMonth !== originalInterventionMonth;

  // When the admin picks "Replanifié", nudge them to change the month.
  React.useEffect(() => {
    if (status !== 'replanifie' || !planningEntry) return;
    // Focus the month input
    document.getElementById('newInterventionMonthInput')?.focus();
  }, [status, planningEntry]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > 20 * 1024 * 1024) {
      setError('Fichier trop volumineux (max 20 Mo)');
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleRemoveFile = (): void => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (status === 'replanifie' && !hasInterventionShift) {
      setError('Tu as choisi "Replanifié" — indique le nouveau mois de l\'intervention.');
      return;
    }

    setSaving(true);
    try {
      const payload: IBacActionCreatePayload = {
        bacId: bac.Id,
        type,
        status,
        comment,
        doneAt: new Date(doneAt).toISOString(),
        planningEntryId: planningEntry ? planningEntry.Id : null,
        newInterventionMonth: hasInterventionShift ? `${interventionMonth}-01` : null,
      };
      await data.createBacAction(payload, file || undefined);
      onSaved();
    } catch (err) {
      setError((err as Error).message || 'Erreur lors de l\'enregistrement');
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <form className={styles.modal} onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <span className={styles.headerIcon}>{meta.icon}</span>
            <div>
              <h3 className={styles.headerTitle}>{meta.title}</h3>
              <p className={styles.headerSubtitle}>
                {bac.DepotTitle} · Bac <strong>{bac.Title}</strong>
              </p>
            </div>
          </div>
          <button type="button" className={styles.close} onClick={onCancel}>×</button>
        </div>

        <div className={styles.body}>
          {planningEntry && (
            <div className={styles.context}>
              <span className={styles.contextLabel}>Déclenché par</span>
              <span className={styles.contextValue}>
                <strong>{planningEntry.Contenu}</strong> prévu en {formatMonthLabel(planningEntry.Mois)}
              </span>
            </div>
          )}

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Statut</span>
            <div className={styles.statusGrid}>
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${styles.statusOption} ${status === opt.value ? styles.statusActive : ''} ${styles['status_' + opt.value]}`}
                  onClick={() => setStatus(opt.value)}
                >
                  <span className={styles.statusLabel}>{opt.label}</span>
                  <span className={styles.statusHint}>{opt.hint}</span>
                </button>
              ))}
            </div>
          </label>

          <div className={styles.row}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Date du {meta.verb.toLowerCase()}</span>
              <input
                type="date"
                className={styles.input}
                value={doneAt}
                onChange={e => setDoneAt(e.target.value)}
                required
              />
            </label>
            {planningEntry && (
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  Mois de l'intervention
                  {hasInterventionShift && (
                    <span className={styles.shiftBadge}>modifié</span>
                  )}
                </span>
                <input
                  id="newInterventionMonthInput"
                  type="month"
                  className={styles.input}
                  value={interventionMonth}
                  onChange={e => setInterventionMonth(e.target.value)}
                />
              </label>
            )}
          </div>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Commentaire</span>
            <textarea
              className={styles.textarea}
              rows={4}
              placeholder="Détails de l'échange, interlocuteur, contexte…"
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Pièce jointe <em className={styles.optional}>(mail, PDF, image… — max 20 Mo)</em></span>
            {file ? (
              <div className={styles.filePreview}>
                <span className={styles.fileIcon}>📎</span>
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{file.name}</div>
                  <div className={styles.fileSize}>{formatBytes(file.size)} · {file.type || 'type inconnu'}</div>
                </div>
                <button type="button" className={styles.fileRemove} onClick={handleRemoveFile}>×</button>
              </div>
            ) : (
              <label className={styles.fileDropzone}>
                <input
                  ref={fileInputRef}
                  type="file"
                  className={styles.fileInput}
                  onChange={handleFileChange}
                />
                <span className={styles.fileDropzoneIcon}>+</span>
                <span className={styles.fileDropzoneText}>
                  Cliquer pour sélectionner un fichier
                </span>
                <span className={styles.fileDropzoneHint}>
                  Mail exporté (.eml, .msg), PDF, image, document…
                </span>
              </label>
            )}
          </label>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.btnGhost} onClick={onCancel} disabled={saving}>
            Annuler
          </button>
          <button type="submit" className={styles.btnPrimary} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
};
